import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckOutlined, GlobalOutlined } from '@ant-design/icons'
import { setLanguage } from '../i18n'

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'mn', label: 'Монгол', flag: '🇲🇳' },
] as const

/**
 * Language switcher for the top bar: a globe with the current code, opening a
 * short list. Two options could have been a toggle, but naming both makes the
 * choice obvious to someone who cannot read the language currently showing.
 */
export default function LanguageMenu() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const active = i18n.language.startsWith('mn') ? 'mn' : 'en'

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="lang" ref={wrap}>
      <button
        className="icon-btn lang-btn"
        onClick={() => setOpen(v => !v)}
        aria-label={t('language')}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('language')}
      >
        <GlobalOutlined />
        <span>{active.toUpperCase()}</span>
      </button>

      {open && (
        <div className="popover lang-menu" role="menu">
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              role="menuitemradio"
              aria-checked={l.code === active}
              className={l.code === active ? 'on' : ''}
              onClick={() => { setLanguage(l.code); setOpen(false) }}
            >
              <span className="flag" aria-hidden>{l.flag}</span>
              <span>{l.label}</span>
              <CheckOutlined className="tick" aria-hidden />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
